import React from 'react';
import {useInfiniteFetchVacancies, type FetchVacanciesQuery, type Vacancy} from '@rocket-ui/lk-api';
import {Link} from 'react-router-dom';
import {useOfferApplyAutoReplyFlow} from '../OfferApplyAutoReplyFlow';
import {OfferActionCell} from './OfferActionCell';
import {OffersMobileCard} from './OffersMobileCard';
import {OffersEmptyState} from '../OffersEmptyState';
import type {OffersTab} from '../../model/offersFilters';
import {Table, TableColumn} from 'ui';
import styles from './OffersTable.module.css';

function getVacancyTitle(vacancy: Vacancy): string {
	return vacancy.name ?? 'Без названия';
}

function getVacancyCustomer(vacancy: Vacancy): string {
	return vacancy.customer?.name ?? 'Не указан';
}

function getVacancyPrice(vacancy: Vacancy): string {
	const amountCents = vacancy.reward?.amountCents;
	if (amountCents == null) return '—';

	return `${new Intl.NumberFormat('ru-RU').format(amountCents / 100).replace(/\u00A0/g, ' ')} ₽`;
}

function getVacancyDeadline(periodTo?: string): string {
	if (!periodTo) return '—';

	const date = new Date(periodTo);
	if (Number.isNaN(date.getTime())) return '—';

	const currentYear = new Date().getFullYear();
	const formatted = new Intl.DateTimeFormat('ru-RU', {
		day: 'numeric',
		month: 'long',
		...(date.getFullYear() !== currentYear ? {year: 'numeric'} : {}),
	}).format(date);

	return date.getFullYear() !== currentYear ? `до ${formatted} г.` : `до ${formatted}`;
}

interface OffersTableProps {
	query: FetchVacanciesQuery;
	tab: OffersTab;
	onShowAll: () => void;
}

export function OffersTable({query, tab, onShowAll}: OffersTableProps): React.ReactElement {
	const {data, isLoading, isError, error, hasNextPage, isFetchingNextPage, fetchNextPage} = useInfiniteFetchVacancies({
		...query,
		per_page: 25,
	});
	const {applyWithFlow, isApplying, flow} = useOfferApplyAutoReplyFlow();

	const vacancies = data?.pages.flatMap((page) => page.data ?? []) ?? [];
	const loaderRef = React.useRef<HTMLDivElement | null>(null);

	const handleApply = (vacancy: Vacancy) => {
		void applyWithFlow(vacancy);
	};

	const renderOfferLink = (vacancy: Vacancy, children: React.ReactNode, title?: string) => {
		const offerId = vacancy.id;
		if (offerId == null) return <>{children}</>;

		return (
			<Link
				to={`/offers/${offerId}`}
				className={styles.rowLink}
				aria-label={title ? `Открыть предложение: ${title}` : 'Открыть предложение'}
			>
				{children}
			</Link>
		);
	};

	const columns: TableColumn<Vacancy>[] = [
		{
			key: 'title',
			header: 'Заказ',
			headerClassName: 'text-left',
			cellClassName: styles.titleCell,
			renderCell: (vacancy) => {
				const title = getVacancyTitle(vacancy);

				return renderOfferLink(
					vacancy,
					<span className={styles.titleStack} title={title}>
						{vacancy.invited ? <span className={styles.invitedLabel}>Вас пригласили</span> : null}
						<span className={styles.clampTwoLines}>{title}</span>
					</span>,
					title,
				);
			},
		},
		{
			key: 'price',
			header: 'Стоимость',
			headerClassName: 'text-right',
			cellClassName: 'text-right',
			renderCell: (vacancy) => {
				const title = getVacancyTitle(vacancy);
				const price = getVacancyPrice(vacancy);

				return renderOfferLink(vacancy, <span>{price}</span>, title);
			},
		},
		{
			key: 'customer',
			header: 'Заказчик',
			headerClassName: 'text-left',
			renderCell: (vacancy) => {
				const customer = getVacancyCustomer(vacancy);
				const title = getVacancyTitle(vacancy);

				return renderOfferLink(
					vacancy,
					<span className={styles.clampTwoLines} title={customer}>
						{customer}
					</span>,
					title,
				);
			},
		},
		{
			key: 'deadline',
			header: 'Срок выполнения',
			headerClassName: 'text-left',
			renderCell: (vacancy) => {
				const deadline = getVacancyDeadline(vacancy.periodTo);
				const title = getVacancyTitle(vacancy);

				return renderOfferLink(
					vacancy,
					<span className={styles.clampTwoLines} title={deadline}>
						{deadline}
					</span>,
					title,
				);
			},
		},
		{
			key: 'action',
			header: null,
			renderCell: (vacancy) => (
				<OfferActionCell vacancy={vacancy} isApplying={isApplying} onApply={handleApply} />
			),
		},
	];

	React.useEffect(() => {
		if (!hasNextPage) return;

		const loaderElement = loaderRef.current;
		if (!loaderElement) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting && !isFetchingNextPage) {
					void fetchNextPage();
				}
			},
			{
				root: null,
				rootMargin: '200px 0px',
				threshold: 0.01,
			},
		);

		observer.observe(loaderElement);

		return () => observer.disconnect();
	}, [fetchNextPage, hasNextPage, isFetchingNextPage]);

	if (isLoading) return <p className={styles.stateText}>Загрузка...</p>;

	if (isError) {
		return (
			<p className={styles.errorText} role="alert">
				{error instanceof Error ? error.message : 'Не удалось загрузить предложения'}
			</p>
		);
	}

	if (vacancies.length === 0) return <OffersEmptyState tab={tab} onShowAll={onShowAll} />;

	return (
		<>
			<div className="hidden sm:block">
				<Table
					data={vacancies}
					columns={columns}
					getRowKey={(vacancy, index) => vacancy.id ?? `${vacancy.name ?? 'offer'}-${index}`}
					rowClassName="h-[64px] text-base leading-[21px] tracking-[-0.025em]"
					tableClassName={styles.offersTable}
				/>
			</div>
			<ul className={`sm:hidden ${styles.mobileList}`}>
				{vacancies.map((vacancy, index) => (
					<li key={vacancy.id ?? `${vacancy.name ?? 'offer'}-${index}`}>
						<OffersMobileCard vacancy={vacancy} isApplying={isApplying} onApply={handleApply} />
					</li>
				))}
			</ul>
			{hasNextPage && (
				<div ref={loaderRef} className={styles.loaderTrigger}>
					{isFetchingNextPage ? 'Загрузка...' : 'Прокрутите ниже для загрузки еще предложений'}
				</div>
			)}
			{flow}
		</>
	);
}
